import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addPortalMessageReaction,
  fetchPortalConversationSummary,
  fetchPortalConversations,
  fetchPortalMessages,
  pollPortalMessages,
  postPortalConversationSeen,
  postPortalMessage,
  removePortalMessageReaction,
  type ChatMessage,
  type ConversationListItem,
  type ConversationSummary,
  type SendMessagePayload,
} from "../lib/chatApi";
import { applyReactionToggleOptimistic } from "../lib/chatReactionOptimistic";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";

/** Liste des fils : pas besoin de 5s (sature l’API + les connexions HTTP avec le long-poll). */
const PORTAL_CONVERSATIONS_POLL_MS = 45_000;
const PORTAL_CONVERSATIONS_BACKGROUND_POLL_MS = 5 * 60_000;

type PortalConversationsOptions = {
  isOnChatList?: boolean;
};



export const usePortalConversations = (options?: PortalConversationsOptions) => {
  const [isWindowVisible, setIsWindowVisible] = useState<boolean>(() => {
    if (typeof document === "undefined") return true;
    return document.visibilityState === "visible";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      setIsWindowVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const refetchInterval = useMemo(() => {
    if (options?.isOnChatList) return PORTAL_CONVERSATIONS_POLL_MS;
    return isWindowVisible
      ? PORTAL_CONVERSATIONS_BACKGROUND_POLL_MS
      : PORTAL_CONVERSATIONS_BACKGROUND_POLL_MS * 2;
  }, [isWindowVisible, options?.isOnChatList]);

  return useQuery<ConversationListItem[]>({
    queryKey: ["portal-conversations"],
    queryFn: fetchPortalConversations,
    staleTime: 30_000,
    refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}



export const usePortalConversationSummary = (conversationId: string | undefined) => {
  return useQuery<ConversationSummary>({
    queryKey: ["portal-conversation-summary", conversationId],
    queryFn: () => fetchPortalConversationSummary(conversationId ?? ""),
    enabled: Boolean(conversationId),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}



export const usePortalChat = (conversationId: string | undefined) => {
  const queryClient = useQueryClient();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  /** Référence stable : un tableau inline recréé à chaque rendu relançait le long-poll en boucle. */
  const queryKey = useMemo(
    () => ["portal-messages", conversationId] as const,
    [conversationId],
  );

  const messagesQuery = useQuery({
    queryKey,
    queryFn: () => fetchPortalMessages(conversationId ?? ""),
    enabled: Boolean(conversationId),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!conversationId) return;
    void postPortalConversationSeen(conversationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
      })
      .catch(() => {
        // Best effort: ne bloque jamais l'affichage du chat.
      });
  }, [conversationId, queryClient]);

  useEffect(() => {
    if (!conversationId) return;
    const abort = new AbortController();
    let cancelled = false;
    let after: string | undefined;

    const run = async () => {
      while (!cancelled) {
        try {
          const result = await pollPortalMessages(conversationId, after, {
            signal: abort.signal,
          });
          if (cancelled) break;
          after = result.updatedAt;
          if (result.hasUpdates) {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries({
              queryKey: ["portal-conversations"],
            });
            queryClient.invalidateQueries({
              queryKey: ["portal-conversation-summary", conversationId],
            });
          }
        } catch (e) {
          if (cancelled || abort.signal.aborted) break;
          if (isAxiosError(e) && e.code === "ERR_CANCELED") break;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [conversationId, queryClient, queryKey]);

  const sendChainRef = useRef(Promise.resolve());
  const [pendingSendCount, setPendingSendCount] = useState<number>(0);

  const sendMessageAsync = useCallback(
    async (newMessage: SendMessagePayload): Promise<ChatMessage> => {
      if (!conversationId || !activeClientId) {
        throw new Error("Conversation ou client indisponible");
      }

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) => [
        ...old,
        {
          id: tempId,
          conversationId,
          senderId: null,
          senderClientId:
            activeClientId === ALL_CLIENTS_KEY ? null : activeClientId,
          senderLabel: null,
          content: newMessage.content,
          createdAt: new Date().toISOString(),
          status: "sending",
        },
      ]);

      setPendingSendCount((c) => c + 1);

      const run = async (): Promise<ChatMessage> => {
        try {
          const serverMessage = await postPortalMessage(
            conversationId,
            newMessage,
          );
          queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
            old.map((m) => (m.id === tempId ? serverMessage : m)),
          );
          return serverMessage;
        } catch (e) {
          queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
            old.filter((m) => m.id !== tempId),
          );
          throw e;
        } finally {
          setPendingSendCount((c) => Math.max(0, c - 1));
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
          queryClient.invalidateQueries({
            queryKey: ["portal-conversation-summary", conversationId],
          });
        }
      };

      const task = sendChainRef.current.then(() => run());
      sendChainRef.current = task.then(
        () => undefined,
        () => undefined,
      );
      return task;
    },
    [conversationId, activeClientId, queryClient, queryKey],
  );

  const sendMessage = useCallback(
    (newMessage: SendMessagePayload) => {
      void sendMessageAsync(newMessage);
    },
    [sendMessageAsync],
  );

  const toggleReactionMutation = useMutation({
    mutationFn: async (args: {
      messageId: string;
      emoji: string;
      reactedByMe: boolean;
    }) => {
      if (args.reactedByMe) {
        return removePortalMessageReaction(
          conversationId ?? "",
          args.messageId,
          {
            emoji: args.emoji,
          },
        );
      }
      return addPortalMessageReaction(conversationId ?? "", args.messageId, {
        emoji: args.emoji,
      });
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ChatMessage[]>(queryKey);
      queryClient.setQueryData<ChatMessage[]>(queryKey, (old = []) =>
        applyReactionToggleOptimistic(
          old,
          args.messageId,
          args.emoji,
          args.reactedByMe,
        ),
      );
      return { previous };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
      queryClient.invalidateQueries({
        queryKey: ["portal-conversation-summary", conversationId],
      });
    },
  });

  return {
    messages: messagesQuery.data ?? [],
    isLoading: messagesQuery.isLoading,
    isFetching: messagesQuery.isFetching,
    sendMessage,
    sendMessageAsync,
    isSending: pendingSendCount > 0,
    toggleReaction: toggleReactionMutation.mutate,
    isTogglingReaction: toggleReactionMutation.isPending,
  };
}
